import React, { useRef, useState } from "react";

import { Button, Icon, Input, Notification, Select } from "@stellar/design-system";

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "ny", label: "Chichewa" },
  { value: "tum", label: "Chitumbuka" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "ht", label: "Kreyòl Ayisyen" },
  { value: "uk", label: "Українська" },
];

interface Step1OrgProfileProps {
  orgName: string;
  setOrgName: (value: string) => void;
  privacyPolicyLink: string;
  setPrivacyPolicyLink: (value: string) => void;
  language: string;
  setLanguage: (value: string) => void;
  logoPreview: string | undefined;
  onLogoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  loading: boolean;
  error: string | undefined;
  onNext: () => void;
}

const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const Step1OrgProfile: React.FC<Step1OrgProfileProps> = ({
  orgName,
  setOrgName,
  privacyPolicyLink,
  setPrivacyPolicyLink,
  language,
  setLanguage,
  logoPreview,
  onLogoChange,
  loading,
  error,
  onNext,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [privacyPolicyLinkError, setPrivacyPolicyLinkError] = useState<string | undefined>();

  return (
    <div className="Onboarding__card">
      <p className="Onboarding__cardTitle">Set up your organization profile</p>
      <p className="Onboarding__cardDescription">
        This logo and name will appear in all invitation emails and SMS messages to help users
        recognize your organization.
      </p>

      <div className="Onboarding__logoUpload">
        <button
          type="button"
          className="Onboarding__logoCircle"
          onClick={() => fileInputRef.current?.click()}
        >
          {logoPreview ? (
            <img src={logoPreview} alt="Organization logo preview" />
          ) : (
            <Icon.User01 />
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          onChange={onLogoChange}
          hidden
        />
        <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
          Upload logo
        </Button>
      </div>

      {error && (
        <Notification variant="error" title="Error" isFilled>
          {error}
        </Notification>
      )}

      <Input
        fieldSize="md"
        id="org-name"
        name="org-name"
        label="Organization name"
        value={orgName}
        onChange={(e) => setOrgName(e.target.value)}
      />
      <Input
        fieldSize="md"
        id="privacy-policy-link"
        name="privacy-policy-link"
        label="Privacy policy URL (optional)"
        value={privacyPolicyLink}
        onChange={(e) => {
          setPrivacyPolicyLink(e.target.value);
          if (privacyPolicyLinkError) setPrivacyPolicyLinkError(undefined);
        }}
        onBlur={() => {
          if (privacyPolicyLink && !isValidUrl(privacyPolicyLink)) {
            setPrivacyPolicyLinkError("Please enter a valid URL (e.g. https://example.com)");
          }
        }}
        error={privacyPolicyLinkError}
      />
      <Select
        fieldSize="md"
        id="language"
        label="Language for invitations & verification"
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
      >
        {LANGUAGE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>

      <Button
        size="lg"
        variant="primary"
        disabled={!orgName || !!privacyPolicyLinkError}
        isLoading={loading}
        onClick={onNext}
      >
        Next
      </Button>
    </div>
  );
};
